<template>
  <div class="login-page">
    <!-- 登录卡片 -->
    <div class="login-card">
      <div class="login-header">
        <h1 class="system-title">科力园区综合管理系统</h1>
        <p class="system-subtitle">Integrated Management System</p>
      </div>

      <el-form 
        :model="loginUser" 
        :rules="rules" 
        ref="loginForm" 
        label-width="60px" 
        class="login-form"
      >
        <el-form-item label="账号" prop="account">
          <el-input 
            v-model="loginUser.account" 
            placeholder="请输入账号"
            autocomplete="username"
            prefix-icon="el-icon-user"
          ></el-input>
        </el-form-item>
        
        <el-form-item label="密码" prop="password">
          <el-input 
            v-model="loginUser.password" 
            placeholder="请输入密码" 
            type="password"
            autocomplete="current-password"
            @input="checkPasswordStrength"
            prefix-icon="el-icon-lock"
          ></el-input>
          <div v-if="passwordStrength.text" :class="['password-strength', passwordStrength.class]">
            {{ passwordStrength.text }}
          </div>
        </el-form-item>
        
        <el-form-item label="验证码" prop="captcha">
          <div class="captcha-wrapper">
            <el-input 
              v-model="loginUser.captcha" 
              placeholder="请输入验证码"
              maxlength="6"
              prefix-icon="el-icon-picture-outline"
            ></el-input>
            <div class="captcha-box" @click="refreshCaptcha" title="点击刷新">
              {{ captchaText }}
            </div>
          </div>
        </el-form-item>
        
        <el-form-item>
          <div class="action-row">
            <el-checkbox v-model="agreementChecked" class="agree-checkbox">
              我已阅读并同意
            </el-checkbox>
            <div class="link-group">
              <el-link type="primary" :underline="false" href="https://hbklyq.cn/privacy-policy" target="_blank">《隐私政策》</el-link>
              <span>和</span>
              <el-link type="primary" :underline="false" href="https://hbklyq.cn/user-agreement" target="_blank">《用户服务协议》</el-link>
            </div>
          </div>
        </el-form-item>
        
        <el-form-item>
          <el-button 
            type="primary" 
            class="login-btn" 
            @click="doLogin('loginForm')"
            :disabled="!agreementChecked"
          >
            登 录
          </el-button>
        </el-form-item>
        
        <div class="bottom-links">
          <el-link type="primary" :underline="false" @click="forgotPassword">忘记密码？</el-link>
        </div>
      </el-form>

      <!-- 安全提示 -->
      <div class="security-tips">
        <i class="el-icon-shield"></i>
        <span>首次登录请修改初始密码，定期更换保障账户安全</span>
      </div>
    </div>

    <!-- 底部版权 -->
    <div class="footer-info">
      <div class="links">
        <el-link type="primary" class="footer-link" :underline="false" href="https://hbklyq.cn/privacy-policy">
          隐私政策
        </el-link>
        <span class="line">|</span>
        <el-link type="primary" class="footer-link" :underline="false" href="https://hbklyq.cn/user-agreement">
          用户协议
        </el-link>
      </div>
      <p>© 2026 科力园区 版权所有 | 
        <el-link type="primary" class="footer-link" :underline="false" href="https://beian.miit.gov.cn/" target="_blank">
          皖ICP备2025091835号-2
        </el-link>
      </p>
      <p class="warning">非涉密信息系统，严禁发布、存储、上传涉及国家秘密内容</p>
    </div>
  </div>
</template>

<script>
import jwt_decode from 'jwt-decode'
import userApi from '../api/user'
import {setUser} from '../utils/auth'

export default {
  name: 'login',
  data () {
    return {
      notifyPromise: Promise.resolve(),
      loginUser: {
        account: '',
        password: '',
        captcha: ''
      },
      captchaText: '',
      agreementChecked: false,
      passwordStrength: {
        text: '',
        class: ''
      },
      rules: {
        account: [
          { required: true, message: '账号不能为空', trigger: 'blur' }
        ],
        password: [
          { required: true, message: "密码不能为空", trigger: "blur" },
          { min: 8, message: '密码长度至少 8 位', trigger: 'blur' }
        ],
        captcha: [
          { required: true, message: '验证码不能为空', trigger: 'blur' },
          { len: 5, message: '请输入 5 位验证码', trigger: 'blur' }
        ]
      }
    }
  },
  mounted() {
    this.refreshCaptcha()
  },
  methods: {
    refreshCaptcha() {
      const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
      let captcha = ''
      for (let i = 0; i < 5; i++) {
        captcha += chars.charAt(Math.floor(Math.random() * chars.length))
      }
      this.captchaText = captcha
    },
    
    checkPasswordStrength(value) {
      const password = value
      let text = ''
      let className = ''
      
      if (password.length < 8) {
        text = '密码长度至少 8 位'
        className = 'weak'
      } else if (!/[a-zA-Z]/.test(password) || !/[0-9]/.test(password)) {
        text = '密码强度：弱（建议包含字母和数字）'
        className = 'weak'
      } else if (password.length >= 12 && /[!@#$%^&*]/.test(password)) {
        text = '密码强度：强'
        className = 'strong'
      } else {
        text = '密码强度：中'
        className = 'medium'
      }
      
      this.passwordStrength = { text, class: className }
    },
    
    forgotPassword() {
      this.$confirm('请联系系统管理员重置密码', '忘记密码', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'info'
      }).then(() => {
        this.$message.info('请联系管理员')
      }).catch(() => {})
    },
    
    checkAgreement() {
      if (!this.agreementChecked) {
        this.$message.warning('请先同意隐私政策和用户协议')
        return false
      }
      return true
    },
    
    validateCaptcha() {
      if (this.loginUser.captcha.toUpperCase() !== this.captchaText) {
        this.$message.error('验证码错误')
        this.refreshCaptcha()
        this.loginUser.captcha = ''
        return false
      }
      return true
    },
    
    notify(msg) {
      this.notifyPromise = this.notifyPromise.then(this.$nextTick).then(() => {
        this.$notify.info({
          title: "公告",
          message: msg,
          offset: 70
        })
      })
    },
    
    isEmpty(value) {
      return (
        value === undefined ||
        value === null ||
        (typeof value === "object" && Object.keys(value).length === 0) ||
        (typeof value === "string" && value.trim().length === 0)
      )
    },
    
    doLogin(formName) {
      if (!this.checkAgreement()) return
      
      this.$refs[formName].validate((valid) => {
        if (!valid) return false
        if (!this.validateCaptcha()) return
        
        let pojo = {
          account: this.loginUser.account,
          password: this.loginUser.password,
          captcha: this.loginUser.captcha.toUpperCase()
        }
        
        userApi.login(pojo).then(res => {
          if (res.data.flag) {
            let { systemRole, userInfo, token } = res.data
            setUser(systemRole, userInfo.name, userInfo.id, token)
            const decode = jwt_decode(token)
            
            this.$store.dispatch("setIsAutnenticated", !this.isEmpty(decode))
            this.$store.dispatch("setUser", decode)
            
            this.$message.success("登录成功")
            this.$router.push("/index")
          } else {
            this.$message.error("账号或密码错误")
            this.loginUser.password = ''
            this.refreshCaptcha()
          }
        }).catch(() => {
          this.$message.error("登录失败，请稍后重试")
          this.refreshCaptcha()
        })
      })
    },
  }
}
</script>

<style scoped>
/* 页面整体 */
.login-page {
  width: 100vw;
  height: 100vh;
  position: relative;
  background: url(../assets/bg.jpg) no-repeat center;
  background-size: cover;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 登录卡片 */
.login-card {
  width: 420px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 40px 35px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(5px);
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* 标题区域 */
.login-header {
  text-align: center;
  margin-bottom: 30px;
}

.system-title {
  font-size: 26px;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
  letter-spacing: 1px;
}

.system-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 6px;
}

/* 表单 */
.login-form {
  width: 100%;
}

/* 验证码 */
.captcha-wrapper {
  display: flex;
  gap: 10px;
  align-items: center;
}

.captcha-wrapper >>> .el-input {
  flex: 1;
}

.captcha-box {
  width: 110px;
  height: 38px;
  background: linear-gradient(45deg, #f5f7fa 25%, #eef2f5 25%, #eef2f5 50%, #f5f7fa 50%);
  background-size: 20px 20px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  font-weight: bold;
  color: #3b82f6;
  letter-spacing: 4px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
}

.captcha-box:hover {
  border-color: #3b82f6;
  box-shadow: 0 0 6px rgba(59, 130, 246, 0.2);
}

/* 协议行 */
.action-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
  color: #6b7280;
}

.agree-checkbox {
  margin-right: 0 !important;
}

.link-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 登录按钮 */
.login-btn {
  width: 100%;
  height: 42px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 8px;
  margin-top: 10px;
}

/* 底部链接 */
.bottom-links {
  text-align: right;
  font-size: 12px;
}

/* 密码强度 */
.password-strength {
  font-size: 12px;
  margin-top: 5px;
  padding-left: 60px;
}
.password-strength.weak { color: #f56c6c; }
.password-strength.medium { color: #f59e0b; }
.password-strength.strong { color: #10b981; }

/* 安全提示 */
.security-tips {
  margin-top: 20px;
  padding: 10px 12px;
  background: #eff6ff;
  border-radius: 6px;
  font-size: 12px;
  color: #3b82f6;
  display: flex;
  align-items: center;
  gap: 6px;
}

.security-tips i {
  font-size: 14px;
}

/* 底部版权 */
.footer-info {
  position: absolute;
  bottom: 25px;
  left: 0;
  right: 0;
  text-align: center;
  color: rgba(255, 255, 255, 0.85);
  font-size: 12px;
  line-height: 1.6;
}

.footer-info .links {
  margin-bottom: 4px;
}

.footer-info .line {
  margin: 0 8px;
  color: rgba(255,255,255,0.5);
}

.footer-info .warning {
  color: rgba(255, 255, 255, 0.6);
  margin-top: 4px;
}

/* 底部链接使用主题蓝色（和登录区一致） */
:deep(.footer-info .footer-link) {
  color: #3b82f6 !important;
}
:deep(.footer-info .footer-link:hover) {
  color: #2563eb !important;
}

/* 全局覆盖 */
:deep(.el-input__inner) {
  border-radius: 6px;
  height: 38px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}
</style>